import type { Currency, EntrySnapshot, SnapshotErrorCode } from '@/types/domain'
import { cadAdapter } from '@/core/adapters/cad-adapter'
import { usdAdapter } from '@/core/adapters/usd-adapter'
import { AdapterError, type CurrencyAdapter } from '@/core/adapters/currency-adapter'
import {
  defaultPtaxDateResolver,
  type PtaxDateResolver,
  type PtaxResolveOutcome,
} from './ptax-date-resolver'

export interface ConvertInput {
  moedaOrigem: Currency
  valorOriginal: number
  dataRecebimento: string
}

export interface ConverterDeps {
  resolver?: PtaxDateResolver
  adapters?: Partial<Record<Currency, CurrencyAdapter>>
  now?: () => Date
}

const DEFAULT_ADAPTERS: Record<Currency, CurrencyAdapter> = {
  USD: usdAdapter,
  CAD: cadAdapter,
}

/**
 * Orchestrates the two conversion steps mandated by IN SRF 208/2002 §2°:
 *   1. moeda origem  → USD  via the currency adapter (BoC for CAD)
 *   2. USD           → BRL  via PTAX compra on the resolved date
 *
 * Returns a fully populated EntrySnapshot on success or a pendente/erro
 * snapshot carrying a structured `erro` field on failure. The function
 * never throws; callers persist the snapshot regardless of outcome so the
 * UI can surface the failure and let the user retry.
 */
export function createConverter(deps: ConverterDeps = {}): {
  convert: (input: ConvertInput) => Promise<EntrySnapshot>
} {
  const resolver = deps.resolver ?? defaultPtaxDateResolver
  const adapters = { ...DEFAULT_ADAPTERS, ...(deps.adapters ?? {}) }
  const now = deps.now ?? ((): Date => new Date())

  return {
    async convert(input: ConvertInput): Promise<EntrySnapshot> {
      const ptaxOutcome: PtaxResolveOutcome = await resolver.resolve(input.dataRecebimento)
      if (!ptaxOutcome.ok) {
        return snapshotFromResolverError(ptaxOutcome.error)
      }

      const adapter = adapters[input.moedaOrigem]
      if (!adapter) {
        return {
          status: 'erro',
          erro: {
            codigo: 'OUTRO',
            detalhe: `moeda não suportada: ${input.moedaOrigem}`,
          },
        }
      }

      let usdConversion
      try {
        usdConversion = await adapter.rateAgainstUSD(input.dataRecebimento)
      } catch (err) {
        return snapshotFromAdapterError(err)
      }

      const valorUSD = round(input.valorOriginal * usdConversion.rate, 4)
      const valorBRL = round(valorUSD * ptaxOutcome.value.ptaxCompra, 2)

      return {
        status: 'calculado',
        dataPTAXAplicavel: ptaxOutcome.value.date,
        cotacaoMoedaParaUSD: usdConversion.rate,
        dataCotacaoMoedaParaUSD: usdConversion.effectiveDate,
        cotacaoUSDBRL: ptaxOutcome.value.ptaxCompra,
        valorUSD,
        valorBRL,
        calculadoEm: now().toISOString(),
        fontes: buildFontes(input.moedaOrigem, ptaxOutcome.value.date, usdConversion.effectiveDate),
      }
    },
  }
}

function buildFontes(
  moeda: Currency,
  ptaxDate: string,
  bocDate: string,
): NonNullable<EntrySnapshot['fontes']> {
  const fontes: NonNullable<EntrySnapshot['fontes']> = {
    bcb: { serie: '10813', data: ptaxDate },
  }
  if (moeda === 'CAD') {
    fontes.boc = { grupo: 'FX_RATES_DAILY', serie: 'FXUSDCAD', data: bocDate }
  }
  return fontes
}

function snapshotFromResolverError(
  error: Extract<PtaxResolveOutcome, { ok: false }>['error'],
): EntrySnapshot {
  if (error.code === 'PARAMETRO_INVALIDO') {
    return {
      status: 'erro',
      erro: { codigo: 'OUTRO', detalhe: `parâmetro inválido: ${error.field} (${error.detail})` },
    }
  }
  if (error.code === 'SEM_PUBLICACAO_NO_INTERVALO') {
    return {
      status: 'erro',
      erro: {
        codigo: 'SEM_PUBLICACAO_NO_INTERVALO',
        detalhe: `BCB sem publicação no intervalo ${error.range.start}..${error.range.end}`,
      },
    }
  }
  return {
    status: 'pendente',
    erro: { codigo: snapshotCodeFromCause(error.cause), detalhe: 'BCB indisponível' },
  }
}

function snapshotFromAdapterError(err: unknown): EntrySnapshot {
  if (err instanceof AdapterError) {
    if (err.code === 'SEM_PUBLICACAO_NO_INTERVALO') {
      return {
        status: 'erro',
        erro: { codigo: 'SEM_PUBLICACAO_NO_INTERVALO', detalhe: err.message },
      }
    }
    return {
      status: 'pendente',
      erro: { codigo: snapshotCodeFromAdapterCode(err.code), detalhe: err.message },
    }
  }
  return {
    status: 'pendente',
    erro: {
      codigo: 'OUTRO',
      detalhe: err instanceof Error ? err.message : String(err),
    },
  }
}

function snapshotCodeFromAdapterCode(code: string): SnapshotErrorCode {
  switch (code) {
    case 'NETWORK':
    case 'TIMEOUT':
    case 'ABORTED':
      return 'REDE'
    case 'HTTP_4XX':
    case 'HTTP_5XX':
    case 'RATE_LIMITED':
      return 'API_INDISPONIVEL'
    case 'PARSE':
      return 'OUTRO'
    default:
      return 'OUTRO'
  }
}

function snapshotCodeFromCause(cause: unknown): SnapshotErrorCode {
  if (cause && typeof cause === 'object' && 'code' in cause) {
    return snapshotCodeFromAdapterCode(String((cause as { code: unknown }).code))
  }
  return 'API_INDISPONIVEL'
}

function round(value: number, decimals: number): number {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

export const defaultConverter = createConverter()
